import cn from "classnames"
import { useCallback } from "react"

import { Analytics } from "utils/analytics"
import { Price, Discount, Icon } from "components"
import { Psychologist } from "utils/interfaces"
import { CURRENCY } from "utils/constants"
import { INITIATOR } from "modules/BookingCommon/components/DatePicker/constants"
import { getCardName } from "modules/BookingCommon/components/CardsPage/common"
import { CardImage } from "modules/BookingCommon/components/CardsPage/components/CardImage"
import { ServicesGroup } from "utils/helpers/ServicesGroup"
import { useDidMount } from "utils/customHooks"
import { useShowPrice } from "utils/hooks/useShowPrice"

interface Props {
    singleServiceAvailable: boolean
    group: ServicesGroup
    square: boolean
    serviceGroup: ServicesGroup | undefined
    psy: Psychologist
    currency: CURRENCY
    showDiscount: boolean
    initiator: INITIATOR
    hasSlots?: boolean
    onChangeServiceGroup: (card: ServicesGroup) => void
    onChangeIsChooseManuallyService?: (value: boolean) => void
}

export function Card({
    singleServiceAvailable,
    group,
    square,
    serviceGroup,
    psy,
    currency,
    showDiscount,
    initiator,
    hasSlots,
    onChangeServiceGroup,
    onChangeIsChooseManuallyService,
}: Props) {
    const isShowPrice = useShowPrice()
    const reasonSource = hasSlots ? group?.availableStatus : group.availableStatusWithCompany
    const available = reasonSource?.ok

    const selected = serviceGroup?.id === group.id
    const cardClickHandler = useCallback(() => {
        if (!available) return
        if (initiator === INITIATOR.CLIENT) {
            Analytics.form_of_work_switched()
            onChangeIsChooseManuallyService?.(true)
        }
        onChangeServiceGroup(group)
    }, [available, initiator])

    useDidMount(() => {
        if (selected) {
            onChangeServiceGroup(serviceGroup)
        }
    })

    return (
        <div
            onClick={cardClickHandler}
            className={cn("booking-cards__card", {
                "booking-cards__card-square": square,
                "is-selected": selected,
                "is-disabled": !available,
                "is-single": singleServiceAvailable,
                "booking-cards__card__with-discount": showDiscount && singleServiceAvailable,
            })}
        >
            <div className="booking-cards__text-and-icon">
                <div className="booking-cards__text-and-icon-subject">
                    {getCardName(group, initiator)}
                </div>
                {isShowPrice && (
                    <div
                        className={cn("booking-cards__session-info-price-container", {
                            "booking-cards__session-info-price-container-column-discount":
                                singleServiceAvailable,
                            "is-nowrap-desktop": available,
                        })}
                    >
                        <div className="booking-cards__text-and-icon-price">
                            {!available ? (
                                reasonSource.reason
                            ) : (
                                <Price
                                    psy={psy}
                                    services={group.services}
                                    currency={currency}
                                    capitalLetter={false}
                                />
                            )}
                        </div>{" "}
                        {group.services.length > 0 && (
                            <Discount
                                psy={psy}
                                services={group.services}
                                className={cn("booking-cards__session-info-price-discount", {
                                    "booking-cards__session-info-price-discount-column-discount":
                                        singleServiceAvailable,
                                })}
                                capitalLetter={false}
                            />
                        )}
                    </div>
                )}
            </div>
            <CardImage serviceGroup={group} className="booking-cards__card-illustration" />
            <div
                className={cn("booking-cards__card__icon", {
                    "booking-cards__card__icon-square": square,
                    "booking-cards__card__icon__one-element": singleServiceAvailable,
                    "is-selected": selected,
                })}
            >
                <Icon type="circle-check" size={24} />
            </div>
        </div>
    )
}
